/**
 * Turn rendered HTML back into Markdown.
 *
 * Claude renders assistant messages as ordinary semantic HTML (`<h2>`, `<pre><code>`,
 * `<ul>`, `<table>`…), so converting the rendered DOM gets the Markdown back
 * without depending on any of Claude's class names or React internals. That
 * matters: selectors are the part of an extension that breaks when the host site
 * ships a redesign, and this path has none.
 *
 * A dependency like Turndown would do the same job, but it is ~30kB for a
 * conversion that only ever has to handle the subset of HTML a Markdown renderer
 * can emit in the first place — which is exactly the subset below.
 */

/** Elements whose content is markup we drop rather than transcribe. */
const SKIPPED = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'SVG', 'BUTTON']);

/**
 * Characters that would be read as Markdown syntax if they survived verbatim.
 * Only escaped in text nodes — never inside code, where a backslash would become
 * part of the code itself.
 */
function escapeText(text: string): string {
  return text.replace(/([\\`*_[\]<>])/g, '\\$1');
}

/** Collapse HTML's insignificant whitespace the way a browser lays it out. */
function collapse(text: string): string {
  return text.replace(/\s+/g, ' ');
}

/** Indent every line after the first, so nested blocks stay inside their parent. */
function indentContinuation(text: string, prefix: string): string {
  return text.split('\n').join(`\n${prefix}`);
}

/** Longest run of backticks in a string — what any delimiter has to beat. */
function longestBacktickRun(text: string): number {
  return [...text.matchAll(/`+/g)].reduce((max, m) => Math.max(max, m[0].length), 0);
}

/** The fence must be longer than any backtick run inside, or it closes early. */
function fenceFor(code: string): string {
  return '`'.repeat(Math.max(3, longestBacktickRun(code) + 1));
}

/**
 * Normalize the blank lines a block leaves behind.
 *
 * Blocks emit generous padding while serializing, which is what makes each
 * branch independent of what came before it. Anything that wraps block content
 * in a prefix — a list marker, a blockquote's `>` — has to tidy first, or it
 * indents the padding too.
 */
function tidy(text: string): string {
  return text
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function languageOf(el: Element): string {
  const source = el.querySelector('code') ?? el;
  for (const cls of source.classList) {
    const match = /^(?:language|lang)-(.+)$/.exec(cls);
    if (match?.[1]) return match[1];
  }
  return '';
}

function serializeChildren(node: Node): string {
  let out = '';
  for (const child of node.childNodes) out += serialize(child);
  return out;
}

function serializeTable(el: Element): string {
  const rows = [...el.querySelectorAll('tr')].map((tr) =>
    [...tr.querySelectorAll('th, td')].map((cell) =>
      // A literal pipe would split the cell, and a newline would end the row.
      serializeChildren(cell)
        .replace(/\|/g, '\\|')
        .replace(/\s*\n\s*/g, ' ')
        .trim(),
    ),
  );
  if (rows.length === 0) return '';

  const width = rows.reduce((max, row) => Math.max(max, row.length), 0);
  const pad = (row: string[]): string =>
    `| ${[...row, ...Array(width - row.length).fill('')].join(' | ')} |`;
  // `rows` is non-empty (checked above), so the header is always there; the
  // fallback only exists to satisfy the index signature.
  const [header = [], ...body] = rows;
  return [pad(header), `| ${Array(width).fill('---').join(' | ')} |`, ...body.map(pad)].join('\n');
}

function serializeList(el: Element): string {
  const ordered = el.tagName === 'OL';
  const start = ordered ? Number(el.getAttribute('start') ?? '1') || 1 : 1;

  const items = [...el.children]
    .filter((child) => child.tagName === 'LI')
    .map((li, index) => {
      const marker = ordered ? `${start + index}. ` : '- ';
      // A renderer wraps list items in <p> only for a loose list, where the
      // blank lines between items are meaningful. Without <p> the list is tight,
      // and keeping the padding would turn it back into a loose one.
      const loose = li.querySelector(':scope > p') !== null;
      const body = tidy(serializeChildren(li));
      const spaced = loose ? body : body.replace(/\n{2,}/g, '\n');
      return marker + indentContinuation(spaced, ' '.repeat(marker.length));
    });

  return items.join('\n');
}

function serializeElement(el: Element): string {
  const tag = el.tagName;

  if (SKIPPED.has(tag) || el.getAttribute('aria-hidden') === 'true') return '';

  switch (tag) {
    case 'BR':
      return '\n';
    case 'HR':
      return '\n\n---\n\n';

    case 'H1':
    case 'H2':
    case 'H3':
    case 'H4':
    case 'H5':
    case 'H6':
      return `\n\n${'#'.repeat(Number(tag[1]))} ${serializeChildren(el).trim()}\n\n`;

    case 'P':
    case 'DIV':
    case 'SECTION':
    case 'ARTICLE':
      return `\n\n${serializeChildren(el)}\n\n`;

    case 'STRONG':
    case 'B': {
      const inner = serializeChildren(el).trim();
      return inner ? `**${inner}**` : '';
    }
    case 'EM':
    case 'I': {
      const inner = serializeChildren(el).trim();
      return inner ? `*${inner}*` : '';
    }
    case 'DEL':
    case 'S': {
      const inner = serializeChildren(el).trim();
      return inner ? `~~${inner}~~` : '';
    }

    case 'CODE': {
      // A <code> inside <pre> is handled by the PRE branch; this is inline code.
      const text = el.textContent ?? '';
      if (!text) return '';
      const ticks = '`'.repeat(longestBacktickRun(text) + 1);
      // Pad when the code itself starts or ends with a backtick, per CommonMark.
      const pad = text.startsWith('`') || text.endsWith('`') ? ' ' : '';
      return `${ticks}${pad}${text}${pad}${ticks}`;
    }

    case 'PRE': {
      const code = (el.textContent ?? '').replace(/\n$/, '');
      const fence = fenceFor(code);
      return `\n\n${fence}${languageOf(el)}\n${code}\n${fence}\n\n`;
    }

    case 'BLOCKQUOTE': {
      const inner = tidy(serializeChildren(el));
      if (!inner) return '';
      return `\n\n${inner
        .split('\n')
        .map((line) => (line ? `> ${line}` : '>'))
        .join('\n')}\n\n`;
    }

    case 'UL':
    case 'OL':
      return `\n\n${serializeList(el)}\n\n`;
    case 'LI':
      // Only reached for a stray <li> outside a list; lists handle their own.
      return `\n- ${serializeChildren(el).trim()}\n`;

    case 'A': {
      const inner = serializeChildren(el).trim();
      const href = el.getAttribute('href') ?? '';
      if (!inner) return '';
      // A link with no destination, or an in-page anchor, is worth less as a
      // broken link than as the plain words it wraps.
      return href && !href.startsWith('#') ? `[${inner}](${href})` : inner;
    }

    case 'IMG': {
      const src = el.getAttribute('src') ?? '';
      const alt = el.getAttribute('alt') ?? '';
      return src ? `![${alt}](${src})` : '';
    }

    case 'TABLE':
      return `\n\n${serializeTable(el)}\n\n`;

    default:
      return serializeChildren(el);
  }
}

function serialize(node: Node): string {
  if (node.nodeType === 3 /* Node.TEXT_NODE */) {
    return escapeText(collapse(node.nodeValue ?? ''));
  }
  if (node.nodeType === 1 /* Node.ELEMENT_NODE */) {
    return serializeElement(node as Element);
  }
  // Documents and fragments carry no markup of their own — the caller's root is
  // often a DocumentFragment (a parsed fragment, or a Range's contents), so this
  // has to descend rather than treat an unknown node type as empty.
  return serializeChildren(node);
}

/** Convert a DOM subtree to Markdown. */
export function htmlToMarkdown(root: Node): string {
  return tidy(
    serialize(root)
      .split('\n')
      .map((line) => line.replace(/[ \t]+$/, ''))
      .join('\n'),
  );
}

/** Convert an HTML fragment, for callers holding markup rather than a live DOM. */
export function htmlStringToMarkdown(html: string): string {
  const template = document.createElement('template');
  template.innerHTML = html;
  return htmlToMarkdown(template.content);
}
